import { useState, useRef } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supportTicketImagesApi } from '@/services/api'
import Button from '@/components/ui/Button'
import Alert from '@/components/ui/Alert'
import Modal from '@/components/ui/Modal'
import { Upload, X, Image as ImageIcon, ChevronLeft, ChevronRight } from 'lucide-react'

interface SupportTicketImageUploadProps {
  ticketId: number
}

export default function SupportTicketImageUpload({ ticketId }: SupportTicketImageUploadProps) {
  const queryClient = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [error, setError] = useState('')
  const [uploadingCount, setUploadingCount] = useState(0)
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null)

  const { data: imagesData } = useQuery({
    queryKey: ['support-ticket-images', ticketId],
    queryFn: () => supportTicketImagesApi.list(ticketId),
  })

  const images = imagesData?.data?.images || []
  const canUpload = images.length < 25

  const uploadMutation = useMutation({
    mutationFn: ({ ticketId, formData }: { ticketId: number; formData: FormData }) =>
      supportTicketImagesApi.upload(ticketId, formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['support-ticket-images', ticketId] })
      setUploadingCount((prev) => prev - 1)
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Failed to upload image')
      setUploadingCount((prev) => prev - 1)
      setTimeout(() => setError(''), 5000)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (imageId: number) => supportTicketImagesApi.delete(imageId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['support-ticket-images', ticketId] })
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Failed to delete image')
      setTimeout(() => setError(''), 5000)
    },
  })

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    setError('')

    if (files.length === 0) return

    // Check total count
    if (images.length + files.length > 25) {
      setError(`Can only upload ${25 - images.length} more image(s). Maximum 25 images per ticket.`)
      return
    }

    // Validate each file
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        setError('Only image files are allowed')
        return
      }

      if (file.size > 1048576) {
        // 1MB
        setError('Each image must be less than 1MB')
        return
      }
    }

    // Upload all files
    setUploadingCount(files.length)
    for (const file of files) {
      const formData = new FormData()
      formData.append('ticket_id', ticketId.toString())
      formData.append('image', file)

      uploadMutation.mutate({ ticketId, formData })
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleDelete = (imageId: number, e?: React.MouseEvent) => {
    if (e) e.stopPropagation()
    if (confirm('Are you sure you want to delete this image?')) {
      deleteMutation.mutate(imageId)
    }
  }

  const handleImageClick = (index: number) => {
    setSelectedImageIndex(index)
  }

  const handlePreviousImage = () => {
    if (selectedImageIndex !== null && selectedImageIndex > 0) {
      setSelectedImageIndex(selectedImageIndex - 1)
    }
  }

  const handleNextImage = () => {
    if (selectedImageIndex !== null && selectedImageIndex < images.length - 1) {
      setSelectedImageIndex(selectedImageIndex + 1)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') {
      handlePreviousImage()
    } else if (e.key === 'ArrowRight') {
      handleNextImage()
    } else if (e.key === 'Escape') {
      setSelectedImageIndex(null)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Attachments ({images.length}/25)</h3>
        {canUpload && (
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileSelect}
              className="hidden"
            />
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingCount > 0}
            >
              <Upload className="w-4 h-4 mr-2" />
              {uploadingCount > 0 ? `Uploading ${uploadingCount}...` : 'Upload Images'}
            </Button>
          </div>
        )}
      </div>

      {error && <Alert type="error" message={error} onClose={() => setError('')} />}

      {!canUpload && (
        <Alert
          type="warning"
          message="Maximum of 25 images reached. Delete an image to upload a new one."
        />
      )}

      {/* Images Grid */}
      {images.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {images.map((image: any, index: number) => (
            <div key={image.id} className="relative group">
              <img
                src={image.image_url}
                alt="Support ticket attachment"
                className="w-full h-32 object-cover rounded-lg border border-gray-200 cursor-pointer hover:opacity-90 transition-opacity"
                onClick={() => handleImageClick(index)}
              />
              <button
                type="button"
                onClick={(e) => handleDelete(image.id, e)}
                className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                disabled={deleteMutation.isPending}
              >
                <X className="w-4 h-4" />
              </button>
              <div className="absolute bottom-2 left-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded">
                {(image.file_size / 1024).toFixed(0)} KB
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
          <ImageIcon className="w-12 h-12 text-gray-400 mx-auto mb-2" />
          <p className="text-sm text-gray-500">No images attached yet</p>
          <p className="text-xs text-gray-400 mt-1">Click "Upload Images" to add attachments</p>
        </div>
      )}

      <p className="text-xs text-gray-500">
        Images are automatically resized to a maximum of 1920x1080 pixels. Each image must be under
        1MB.
      </p>

      {/* Image Viewer Modal */}
      {selectedImageIndex !== null && images[selectedImageIndex] && (
        <Modal
          title={`Image ${selectedImageIndex + 1} of ${images.length}`}
          onClose={() => setSelectedImageIndex(null)}
          size="xl"
        >
          <div className="relative" onKeyDown={handleKeyDown} tabIndex={0}>
            <div className="flex items-center justify-center bg-gray-100 rounded-lg min-h-[400px] max-h-[600px]">
              <img
                src={images[selectedImageIndex].image_url}
                alt={`Support ticket attachment ${selectedImageIndex + 1}`}
                className="max-w-full max-h-[600px] object-contain"
              />
            </div>

            {/* Navigation Buttons */}
            {images.length > 1 && (
              <>
                <button
                  onClick={handlePreviousImage}
                  disabled={selectedImageIndex === 0}
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                  aria-label="Previous image"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <button
                  onClick={handleNextImage}
                  disabled={selectedImageIndex === images.length - 1}
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                  aria-label="Next image"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              </>
            )}

            {/* Image Info */}
            <div className="mt-4 flex justify-between items-center text-sm text-gray-600">
              <span>{(images[selectedImageIndex].file_size / 1024).toFixed(0)} KB</span>
              <Button
                variant="secondary"
                size="sm"
                onClick={(e) => {
                  handleDelete(images[selectedImageIndex].id, e)
                  setSelectedImageIndex(null)
                }}
                disabled={deleteMutation.isPending}
              >
                <X className="w-4 h-4 mr-2" />
                Delete Image
              </Button>
            </div>

            <p className="text-xs text-gray-500 mt-2 text-center">
              Use arrow keys to navigate, ESC to close
            </p>
          </div>
        </Modal>
      )}
    </div>
  )
}
